import type { GuardianPlayerSessionDetail } from '@/features/guardian-player/services';
import { sortProgressCheckpoints } from '@/features/player/components/player-profile/player-progress-tracker/progress-tracker.utils';
import {
  formatProgressBadgeTitleCompact,
  formatProgressCheckpointTitleOverview,
} from '@/features/player/utils/progress-title';

type SessionDetailAttempt = GuardianPlayerSessionDetail['attempts'][number];
type SessionDetailPin = GuardianPlayerSessionDetail['pins'][number] & {
  badgeName: string;
};
type SortableSessionDetailAttempt = SessionDetailAttempt & {
  name: string;
  status: GuardianSessionAttemptStatus;
};

export type GuardianSessionAttemptStatus = 'completed' | 'incomplete';
export type GuardianSessionDetailLevelSection = {
  level: number;
  pins: SessionDetailPin[];
  attempts: GuardianPlayerSessionDetail['attempts'];
};
export type GuardianSessionDetailBadgeSection = {
  name: string;
  pin?: SessionDetailPin;
  attempts: GuardianPlayerSessionDetail['attempts'];
};

export function isGuardianSessionOverallPinName(name: string) {
  return name.trim().toLowerCase() === 'overall';
}

export function isGuardianSessionAttemptCompleted(status: string) {
  const normalized = status.trim().toLowerCase();
  return normalized === 'completed' || normalized === 'complete';
}

export function getGuardianSessionAttemptStatus(attempts: string[]): GuardianSessionAttemptStatus {
  return attempts.some(isGuardianSessionAttemptCompleted) ? 'completed' : 'incomplete';
}

export function getGuardianSessionBadgeKey(level: number, badgeName: string) {
  const normalizedBadgeName = badgeName.replace(/[\s_-]+/g, '').toLowerCase();
  return `${level}-${normalizedBadgeName}`;
}

function toSortableSessionDetailAttempt(
  attempt: SessionDetailAttempt,
): SortableSessionDetailAttempt {
  return {
    ...attempt,
    name: attempt.checkpointName,
    status: getGuardianSessionAttemptStatus(attempt.attempts),
  };
}

function formatSessionDetailAttempt(attempt: SessionDetailAttempt): SessionDetailAttempt {
  return {
    ...attempt,
    checkpointName: formatProgressCheckpointTitleOverview(attempt.checkpointName, {
      layerSeparator: ': ',
    }),
  };
}

function buildGuardianPlayerSessionDetailAttempts(
  data: GuardianPlayerSessionDetail,
): GuardianPlayerSessionDetail['attempts'] {
  const attemptsByBadge = new Map<string, SortableSessionDetailAttempt[]>();

  data.attempts.forEach(attempt => {
    const sortableAttempt = toSortableSessionDetailAttempt(attempt);
    const badgeKey = getGuardianSessionBadgeKey(sortableAttempt.level, sortableAttempt.badgeName);
    const group = attemptsByBadge.get(badgeKey) ?? [];

    group.push(sortableAttempt);
    attemptsByBadge.set(badgeKey, group);
  });

  return [...attemptsByBadge.values()].flatMap(group =>
    sortProgressCheckpoints(group).map(checkpoint =>
      formatSessionDetailAttempt(checkpoint as SortableSessionDetailAttempt),
    ),
  );
}

export function buildGuardianPlayerSessionDetail(data: GuardianPlayerSessionDetail) {
  return {
    ...data,
    attempts: buildGuardianPlayerSessionDetailAttempts(data),
  };
}

export function buildGuardianSessionDetailLevelSections(
  data?: GuardianPlayerSessionDetail,
): GuardianSessionDetailLevelSection[] {
  if (!data) return [];

  const sectionsByLevel = new Map<number, GuardianSessionDetailLevelSection>();
  const attemptBadgeNamesByLevel = new Map<string, string>();

  const getSection = (level: number) => {
    let section = sectionsByLevel.get(level);

    if (!section) {
      section = { level, pins: [], attempts: [] };
      sectionsByLevel.set(level, section);
    }

    return section;
  };

  data.attempts.forEach(attempt => {
    attemptBadgeNamesByLevel.set(
      formatProgressBadgeTitleCompact(attempt.badgeName),
      attempt.badgeName,
    );
    getSection(attempt.level).attempts.push(attempt);
  });

  data.pins.forEach(pin => {
    getSection(pin.levelNumber).pins.push({
      ...pin,
      badgeName: attemptBadgeNamesByLevel.get(pin.name) ?? pin.name,
    });
  });

  return [...sectionsByLevel.values()].sort((a, b) => a.level - b.level);
}

export function buildGuardianSessionDetailBadgeSections({
  level,
  pins,
  attempts,
}: GuardianSessionDetailLevelSection): GuardianSessionDetailBadgeSection[] {
  const badgeSections = new Map<string, GuardianSessionDetailBadgeSection>();

  pins
    .filter(pin => !isGuardianSessionOverallPinName(pin.name))
    .forEach(pin => {
      badgeSections.set(getGuardianSessionBadgeKey(level, pin.badgeName), {
        name: pin.badgeName,
        pin,
        attempts: [],
      });
    });

  attempts
    .filter(attempt => !isGuardianSessionOverallPinName(attempt.badgeName))
    .forEach(attempt => {
      const key = getGuardianSessionBadgeKey(level, attempt.badgeName);
      const section = badgeSections.get(key);

      if (section) {
        section.attempts.push(attempt);
        return;
      }

      badgeSections.set(key, {
        name: attempt.badgeName,
        attempts: [attempt],
      });
    });

  return [...badgeSections.values()];
}
